import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, media } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Package, FlaskConical, ArrowRight, Flame } from "lucide-react";

const TYPES = [
  { key: "", label: "All Products" },
  { key: "vial", label: "Peptides" },
  { key: "supply", label: "Supplies" },
  { key: "case", label: "Cases & Accessories" },
];

const POPULAR = ["tirzepatide", "retatrutide", "bpc-tb-blend", "semaglutide", "nad-plus", "ghk-cu"];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [cat, setCat] = useState("");
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();

  useEffect(() => {
    setCat(params.get("category") || "");
    setLoading(true);
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
  }, [params]);

  const cats = useMemo(() => [...new Set(products.flatMap((p) => p.categories || []))].sort(), [products]);
  const popular = useMemo(() => POPULAR.map((s) => products.find((p) => p.slug === s)).filter(Boolean), [products]);

  let filtered = products.filter((p) => {
    const ql = q.toLowerCase().replace(/[^a-z0-9]/g, "");
    const name = (p.name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const matchesQ = !ql || name.includes(ql);
    const matchesT = !type || (type === "vial" ? (p.product_type !== "case" && p.product_type !== "supply") : p.product_type === type);
    const matchesC = !cat || (p.categories || []).some((c) => c.toLowerCase() === cat.toLowerCase());
    return matchesQ && matchesT && matchesC;
  });
  if (sort === "featured") filtered = filtered.slice().sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
  if (sort === "alpha") filtered = filtered.slice().sort((a, b) => a.name.localeCompare(b.name));
  if (sort === "price_low") filtered = filtered.slice().sort((a, b) => (a.from_price || 0) - (b.from_price || 0));
  if (sort === "price_high") filtered = filtered.slice().sort((a, b) => (b.from_price || 0) - (a.from_price || 0));

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-7xl mx-auto" data-testid="shop-page">
      <div className="mono-label mb-3">Shop / The CG Labz Store</div>
      <h1 className="font-serif text-5xl sm:text-6xl text-white mb-2">The Store</h1>
      <p className="text-slate-400 text-sm max-w-xl mb-8">Research peptides, storage cases and lab accessories — every batch verified, shipped cold-chain ready.</p>

      {/* popular spotlight */}
      {!loading && !q && !type && !cat && popular.length > 0 && (
        <section className="mb-12" data-testid="shop-popular-row">
          <div className="flex items-center gap-2 mb-4">
            <Flame size={16} className="text-amber-400" />
            <h2 className="text-base md:text-lg font-mono uppercase tracking-widest text-amber-200">Popular right now</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {popular.map((p) => (
              <Link key={p.id} to={`/shop/${p.slug}`} data-testid={`popular-card-${p.slug}`}
                className="group relative glass rounded-xl overflow-hidden border border-amber-400/20 hover:border-amber-400/50 transition-all duration-300">
                <div className="relative aspect-square bg-gradient-to-b from-slate-900/40 to-black/60 overflow-hidden flex items-center justify-center">
                  {p.image ? (
                    <img src={media(p.image)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.08] transition-transform duration-700" />
                  ) : <FlaskConical size={28} className="text-slate-600" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>
                <div className="p-3">
                  <div className="font-serif text-sm text-white leading-tight line-clamp-2">{p.name.replace("CG Labz ", "")}</div>
                  {p.from_price != null && (
                    <div className="mt-1.5 flex items-baseline gap-1">
                      <span className="text-[9px] font-mono text-slate-500">from</span>
                      <span className="text-sm font-mono text-amber-200">${p.from_price.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* type tabs */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="shop-type-tabs">
        {TYPES.map((t) => (
          <button key={t.key} onClick={() => setType(t.key)} data-testid={`shop-type-${t.key || "all"}`}
            className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition-all ${type === t.key ? "bg-cyan-400/15 border border-cyan-400/50 text-cyan-200" : "bg-white/5 border border-white/10 text-slate-400 hover:border-cyan-400/30"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* controls */}
      <div className="flex flex-wrap gap-3 mb-10">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" data-testid="shop-search-input"
            className="pl-9 bg-black/40 border-white/10" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="shop-category-filter"
          className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-slate-300">
          <option value="">All categories</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="shop-sort"
          className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-slate-300">
          <option value="featured">Featured</option>
          <option value="alpha">A → Z</option>
          <option value="price_low">Price: low → high</option>
          <option value="price_high">Price: high → low</option>
        </select>
      </div>

      {loading && <div className="text-slate-500 font-mono text-sm animate-pulse">Loading store…</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="shop-grid">
        {filtered.map((p) => {
          const isCase = p.product_type === "case";
          return (
            <Link key={p.id} to={`/shop/${p.slug}`} data-testid={`shop-card-${p.slug}`}
              className="group glass rounded-2xl overflow-hidden border border-white/10 hover:border-cyan-400/40 transition-all duration-300 flex flex-col">
              <div className="relative aspect-square bg-gradient-to-b from-slate-900/40 to-black/50 overflow-hidden">
                {p.image ? (
                  <img src={media(p.image)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-700 ease-out" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-600">{isCase ? <Package size={34} /> : <FlaskConical size={34} />}</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                {p.featured && <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-cyan-400/15 border border-cyan-400/30 text-[9px] font-mono uppercase tracking-widest text-cyan-200">Featured</span>}
                <span className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest ${isCase ? "bg-violet-500/20 border border-violet-400/40 text-violet-200" : p.product_type === "supply" ? "bg-amber-500/15 border border-amber-400/30 text-amber-200" : "bg-black/50 border border-white/15 text-slate-300"}`}>{isCase ? "Case" : p.product_type === "supply" ? "Supply" : "Peptide"}</span>
              </div>
              <div className="p-4 sm:p-5 flex flex-col flex-1">
                <div className="font-serif text-base sm:text-lg text-white leading-tight line-clamp-2">{p.name.replace("CG Labz ", "")}</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(p.categories || []).filter((c) => c !== "Cases").slice(0, 2).map((c) => (
                    <span key={c} className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-mono text-slate-400">{c}</span>
                  ))}
                </div>
                <div className="mt-auto pt-4 flex items-center justify-between">
                  {p.from_price != null ? (
                    <div className="flex items-baseline gap-1.5">
                      {!isCase && <span className="text-[10px] font-mono text-slate-500">from</span>}
                      <span className="text-lg font-mono text-white">${p.from_price.toFixed(2)}</span>
                    </div>
                  ) : <span className="text-xs font-mono text-slate-500">View</span>}
                  <span className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-slate-300 group-hover:border-cyan-400/50 group-hover:text-cyan-300 transition-colors">
                    <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      {!loading && filtered.length === 0 && <div className="text-center py-20 text-slate-500" data-testid="shop-empty">No products matched. Try a different filter.</div>}
    </div>
  );
}
